"use client";
import { useState } from "react";
import { Search, RefreshCw, Contact, ShieldCheck, Phone } from "lucide-react";
import { RequireRole } from "../console-shell";
import { Button, Desk, PageHeader } from ".";
import { ReadState, useRead, type PageResult } from "./record-tools";
type ContactRow = {
  personId: string;
  displayName: string;
  organizationName: string;
  mobile: string | null;
};
export function ContactsWorkspace() {
  return (
    <RequireRole roleCode="GRASSROOTS_INFO_COORDINATOR">
      <ContactsDesk />
    </RequireRole>
  );
}
function ContactsDesk() {
  const [word, setWord] = useState(""),
    [q, setQ] = useState(""),
    [page, setPage] = useState(0),
    [cursors, setCursors] = useState([""]);
  const list = useRead<PageResult<ContactRow>>(
    `/api/v1/member-records/contacts?${new URLSearchParams({ limit: "20", ...(q ? { q } : {}), ...(cursors[page] ? { cursor: cursors[page] } : {}) })}`
  );
  return (
    <Desk wide>
      <PageHeader
        eyebrow="会员与组织 / 联系名册"
        title="联系名册"
        description="查找授权范围内的会员，核对所属组织与联系电话。"
      />
      <div className="import-context">
        <ShieldCheck size={17} />
        <span>仅用于授权范围内的会务联系，每次查询均留存访问记录。</span>
      </div>
      <div className="record-toolbar">
        <form
          className="record-search"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(word.trim());
            setPage(0);
            setCursors([""]);
          }}
        >
          <Search size={17} />
          <input
            aria-label="按会员姓名查找"
            placeholder="输入会员姓名"
            value={word}
            onChange={(e) => setWord(e.target.value)}
            maxLength={60}
          />
          <Button type="submit" variant="outline">
            查找
          </Button>
        </form>
        <Button variant="ghost" aria-label="刷新联系名册" onClick={list.reload}>
          <RefreshCw size={16} />
        </Button>
      </div>
      <ReadState {...list} retry={list.reload} />
      {list.data && (
        <>
          <div className="record-list-meta">
            <span>
              当前页 {list.data.items.length} 位会员{q ? ` · 姓名包含「${q}」` : ""}
            </span>
            <span>按姓名排列</span>
          </div>
          {list.data.items.length ? (
            <div className="record-table-scroll">
              <table className="record-table contacts-table">
                <caption className="sr-only">授权范围内的联系名册</caption>
                <thead>
                  <tr>
                    <th>会员</th>
                    <th>所属组织</th>
                    <th>联系电话</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((row) => (
                    <tr key={row.personId}>
                      <td>
                        <div className="contact-person">
                          <Contact size={18} />
                          <strong>{row.displayName}</strong>
                        </div>
                      </td>
                      <td>{row.organizationName}</td>
                      <td>
                        {row.mobile ? (
                          <span className="contact-phone">
                            <Phone size={14} />
                            {row.mobile}
                          </span>
                        ) : (
                          <span className="record-muted">未登记电话</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="workspace-empty">
              <Contact />
              <strong>没有匹配的会员</strong>
              <p>{q ? "调整姓名后再查找。" : "当前授权范围内没有可显示的联系记录。"}</p>
            </div>
          )}
          <div className="record-pagination">
            <span>第 {page + 1} 页</span>
            <Button variant="outline" disabled={!page} onClick={() => setPage(page - 1)}>
              上一页
            </Button>
            <Button
              variant="outline"
              disabled={!list.data.meta?.nextCursor}
              onClick={() => {
                setCursors((old) => [...old.slice(0, page + 1), list.data!.meta!.nextCursor!]);
                setPage(page + 1);
              }}
            >
              下一页
            </Button>
          </div>
        </>
      )}
    </Desk>
  );
}
